import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';

import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';
import { MaskService } from '../../../../../../shared/services/mask.service';
import { montarFormParcela } from '../venda-pagamento.form';
import { diasEntre, redistribuirParcelas, somarDias, somarValores } from '../venda-pagamento.parcelas';
import {
    VdParcelaDialogComponent,
    VdParcelaDialogData,
    VdParcelaDialogResultado,
} from './vd-parcela-dialog/vd-parcela-dialog.component';

/** O que a redistribuição precisa saber de cada parcela — recorte do FormGroup. */
interface ParcelaParaRedistribuir {
    valor: number;
    isValorFixadoUsuario: boolean;
}

/**
 * Grade de parcelas de UM recebimento (§8.7).
 *
 * A parcela HERDA a forma do recebimento a que pertence: não há coluna "Forma" nem escolha por
 * parcela — os campos dependentes (conta, intermediador, bandeira, categoria) foram definidos uma
 * vez no bloco do recebimento. Por isso o componente recebe o FormGroup do RECEBIMENTO, e não um
 * FormArray solto: ele precisa do `valor` do recebimento para redistribuir.
 *
 * **Redistribuição automática em CENTAVOS, resto na última** (§8.7): adicionar uma parcela ou
 * editar o valor de uma regera as DEMAIS — intermediária espalha para as seguintes, última
 * espalha para as anteriores ainda não fixadas pelo usuário. Por isso não existe mensagem de soma:
 * a soma fecha por construção. A conta mora em `venda-pagamento.parcelas.ts`, pura.
 */
@Component({
    selector: 'app-vd-parcelas',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        EmptyStateComponent,
    ],
    templateUrl: './vd-parcelas.component.html',
    styleUrl: './vd-parcelas.component.css',
})
export class VdParcelasComponent {
    /** O RECEBIMENTO dono destas parcelas (`montarFormRecebimento`). */
    @Input({ required: true }) public recebimento!: FormGroup;

    /** A emissão do documento: os Dias são contados a partir dela (§8.7.1). */
    @Input() public dataEmissao: Date | string | null = null;

    /** NSU só no pedido; o modal esconde o campo no orçamento. */
    @Input() public ehOrcamento: boolean = true;
    @Input() public isSomenteLeitura: boolean = false;

    /** DF10: NSU e observação obrigatórios na parcela quando o contas a receber exige (só no pedido). */
    @Input() public isNsuObrigatorio: boolean = false;
    @Input() public isObservacaoParcelaObrigatoria: boolean = false;

    @Input({ required: true }) public mascaraDinheiro!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraInteira!: Partial<NgxCurrencyConfig>;

    public readonly onDateInput = MaskService.onDateInput;

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog,
    ) {}

    public get parcelas(): FormArray {
        return this.recebimento.get('parcelas') as FormArray;
    }

    public get linhas(): FormGroup[] {
        return this.parcelas.controls as FormGroup[];
    }

    public get valorDoRecebimento(): number {
        return Number(this.recebimento.get('valor')?.value) || 0;
    }

    /** Só para o rodapé informativo: a soma fecha por construção, então isto é conferência visual. */
    public get somaDasParcelas(): number {
        return somarValores(this.linhas.map((l) => l.get('valor')?.value));
    }

    /**
     * Adicionar (§8.7): entra com valor zero e vencimento 30 dias após a última — e a
     * redistribuição a partir dela espalha o saldo para as anteriores não fixadas.
     */
    public adicionar(): void {
        const linha = montarFormParcela(this.fb);
        const ultima = this.linhas[this.linhas.length - 1];
        const diasBase = ultima ? Number(ultima.get('dias')?.value) || 0 : 0;
        const emissao = this.comoData(this.dataEmissao) ?? new Date();

        linha.patchValue(
            {
                numeroParcela: this.linhas.length + 1,
                dias: diasBase + 30,
                dataVencimento: somarDias(emissao, diasBase + 30),
                valor: 0,
            },
            { emitEvent: false },
        );

        this.parcelas.push(linha);
        this.redistribuirAPartirDe(this.linhas.length - 1);
        this.renumerar();
    }

    public remover(indice: number): void {
        this.parcelas.removeAt(indice);
        this.renumerar();

        // Remover redistribui o que sobrou entre as que ficaram (a partir da última).
        if (this.linhas.length) this.redistribuirAPartirDe(this.linhas.length - 1);
    }

    /** Valor editado direto na linha: fixa a parcela e regera as demais (§8.7). */
    public aoSairDoValor(indice: number): void {
        this.redistribuirAPartirDe(indice);
    }

    /** Dias editados na linha: o vencimento acompanha, a partir da emissão. */
    public aoSairDosDias(indice: number): void {
        const linha = this.linhas[indice];
        const dias = Number(linha.get('dias')?.value);
        const emissao = this.comoData(this.dataEmissao);

        if (!emissao || isNaN(dias)) return;

        linha.get('dataVencimento')?.setValue(somarDias(emissao, Math.max(0, dias)), { emitEvent: false });
    }

    /** Vencimento editado na linha: os Dias acompanham (§8.7.1, mesma regra do modal). */
    public aoMudarVencimento(indice: number): void {
        const linha = this.linhas[indice];
        const emissao = this.comoData(this.dataEmissao);
        const vencimento = this.comoData(linha.get('dataVencimento')?.value);

        if (!emissao || !vencimento) return;

        linha.get('dias')?.setValue(diasEntre(emissao, vencimento), { emitEvent: false });
    }

    public temObservacao(linha: FormGroup): boolean {
        return !!String(linha.get('observacao')?.value ?? '').trim();
    }

    /** O que o contas a receber exige e a parcela ainda não tem (DF10): marcador na grade. */
    public pendenciaObrigatoria(linha: FormGroup): string | null {
        const faltas: string[] = [];
        if (this.isNsuObrigatorio && !String(linha.get('nsu')?.value ?? '').trim()) faltas.push('NSU');
        if (this.isObservacaoParcelaObrigatoria && !this.temObservacao(linha)) faltas.push('observação');
        return faltas.length ? `O contas a receber exige ${faltas.join(' e ')} nesta parcela. Edite a parcela para informar.` : null;
    }

    /** O modal edita uma CÓPIA e devolve; aqui aplicamos e redistribuímos se o valor mudou. */
    public editar(indice: number): void {
        const linha = this.linhas[indice];
        const valorAntes = Number(linha.get('valor')?.value) || 0;

        const data: VdParcelaDialogData = {
            parcela: linha,
            numero: indice + 1,
            total: this.linhas.length,
            dataEmissao: this.dataEmissao,
            ehOrcamento: this.ehOrcamento,
            isSomenteLeitura: this.isSomenteLeitura,
            isNsuObrigatorio: this.isNsuObrigatorio,
            isObservacaoObrigatoria: this.isObservacaoParcelaObrigatoria,
            mascaraDinheiro: this.mascaraDinheiro,
        };

        this.dialog
            .open(VdParcelaDialogComponent, { data, width: '520px', maxWidth: '96vw', autoFocus: false })
            .afterClosed()
            .subscribe((resultado: VdParcelaDialogResultado | undefined) => {
                if (!resultado) return;

                linha.patchValue(
                    {
                        dataVencimento: resultado.dataVencimento,
                        dias: resultado.dias,
                        valor: resultado.valor,
                        nsu: resultado.nsu,
                        observacao: resultado.observacao,
                    },
                    { emitEvent: false },
                );

                if (Math.abs(resultado.valor - valorAntes) >= 0.005) this.redistribuirAPartirDe(indice);
            });
    }

    /** Aplica `redistribuirParcelas` (pura) de volta nos FormGroups, sem emitir evento. */
    private redistribuirAPartirDe(indiceEditado: number): void {
        const recorte: ParcelaParaRedistribuir[] = this.linhas.map((l) => ({
            valor: Number(l.get('valor')?.value) || 0,
            isValorFixadoUsuario: l.get('isValorFixadoUsuario')?.value === true,
        }));

        const novo = redistribuirParcelas(recorte, indiceEditado, this.valorDoRecebimento);

        novo.forEach((p, i) => {
            this.linhas[i].patchValue(
                { valor: p.valor, isValorFixadoUsuario: p.isValorFixadoUsuario },
                { emitEvent: false },
            );
        });
    }

    private renumerar(): void {
        this.linhas.forEach((l, i) => l.get('numeroParcela')?.setValue(i + 1, { emitEvent: false }));
    }

    private comoData(valor: unknown): Date | null {
        if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
        if (typeof valor === 'string' && valor) {
            const d = new Date(valor);

            return isNaN(d.getTime()) ? null : d;
        }

        return null;
    }
}
